import { z } from 'zod';
import { ITEM_ID_RE } from './ids.js';
// ---------------------------------------------------------------------------
// Digest report contract — ported verbatim from the daemon's schema.ts. The
// digest JSON already *is* an SDUI tree (sections -> typed blocks -> items); the
// hosted backend stores it as-is in the doc-store and extracts feedback-capable
// items for keying (see §8 of the design).
// ---------------------------------------------------------------------------
export const SOURCE_TYPES = [
    'slack',
    'gmail',
    'linear',
    'sentry',
    'calendar',
    'bigquery',
    'ops_metric',
    'granola',
    'other',
];
export const FEEDBACK_ACTIONS = [
    'mark_done',
    'mark_not_relevant',
    'snooze_until',
    'add_note',
];
// ---------------------------------------------------------------------------
// Item (the feedback-capable unit inside an item_list block)
// ---------------------------------------------------------------------------
export const ItemSchema = z
    .object({
    // identity (required when feedback_enabled — enforced in the write boundary)
    item_id: z.string().regex(ITEM_ID_RE).optional(),
    source_type: z.enum(SOURCE_TYPES).optional(),
    source_ref: z.string().min(1).optional(),
    semantic_slot: z.string().min(1).optional(),
    identity_basis: z.enum(['native_ref', 'synthetic']).optional(),
    feedback_enabled: z.boolean().default(false),
    allowed_feedback: z.array(z.enum(FEEDBACK_ACTIONS)).optional(),
    // Mirror/reference: an informational copy of a canonical item that lives in
    // another section. Carries its own rewritten title/detail but no item_id of
    // its own; the shell reflects the canonical item's feedback status here.
    // Mutually exclusive with feedback_enabled.
    mirrors_item_id: z.string().regex(ITEM_ID_RE).optional(),
    // content
    title: z.string().min(1),
    detail: z.string().optional(),
    meta: z.string().optional(),
    src: z.string().optional(),
    link: z.url().optional(),
    action_link: z.url().optional(),
    action_label: z.string().optional(),
    // Context link: "where this came from / the conversation," distinct from the
    // action. Rendered as a separate labeled link by the shell.
    context_link: z.url().optional(),
    context_label: z.string().optional(),
    // time-bound self-heal hints (rendered/expired client-side)
    starts_at: z.string().optional(),
    ends_at: z.string().optional(),
    prep: z.boolean().optional(),
    prep_label: z.string().optional(),
    // intraday living-document tracking (preserved across refreshes)
    added_at: z.string().optional(),
    first_seen_at: z.string().optional(),
    added_by_run: z.string().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Block catalog (V1)
// ---------------------------------------------------------------------------
const MetricSchema = z
    .object({
    label: z.string(),
    value: z.union([z.string(), z.number()]),
    state: z.enum(['ok', 'warn', 'alert', 'info', 'neutral']).optional(),
    hint: z.string().optional(),
})
    .strict();
const markdownBlock = z
    .object({
    type: z.literal('markdown'),
    body: z.string(),
})
    .strict();
const itemListBlock = z
    .object({
    type: z.literal('item_list'),
    title: z.string().optional(),
    note: z.string().optional(),
    items: z.array(ItemSchema),
})
    .strict();
const calloutBlock = z
    .object({
    type: z.literal('callout'),
    variant: z.enum(['info', 'warn', 'success', 'danger']).default('info'),
    title: z.string().optional(),
    body: z.string(),
})
    .strict();
const metricGridBlock = z
    .object({
    type: z.literal('metric_grid'),
    title: z.string().optional(),
    metrics: z.array(MetricSchema),
})
    .strict();
const tableBlock = z
    .object({
    type: z.literal('table'),
    title: z.string().optional(),
    columns: z.array(z.string()),
    rows: z.array(z.array(z.string())),
})
    .strict();
const timelineBlock = z
    .object({
    type: z.literal('timeline'),
    title: z.string().optional(),
    entries: z.array(z
        .object({
        time: z.string().optional(),
        title: z.string(),
        detail: z.string().optional(),
        tag: z.string().optional(),
        emphasis: z.enum(['normal', 'muted', 'strong']).optional(),
    })
        .strict()),
})
    .strict();
const linkListBlock = z
    .object({
    type: z.literal('link_list'),
    title: z.string().optional(),
    links: z.array(z.object({ label: z.string(), href: z.url() }).strict()),
})
    .strict();
const keyValueBlock = z
    .object({
    type: z.literal('key_value'),
    title: z.string().optional(),
    pairs: z.array(z.object({ key: z.string(), value: z.string() }).strict()),
})
    .strict();
const quoteBlock = z
    .object({
    type: z.literal('quote'),
    body: z.string(),
    cite: z.string().optional(),
})
    .strict();
const barChartBlock = z
    .object({
    type: z.literal('bar_chart'),
    title: z.string().optional(),
    bars: z.array(z
        .object({
        label: z.string(),
        value: z.string().optional(),
        percent: z.number().min(0).max(100),
        state: z.enum(['ok', 'warn', 'alert', 'info', 'neutral']).optional(),
    })
        .strict()),
})
    .strict();
// Horizontal stacked bars: one bar per category, segmented by status/series.
const stackedBarChartBlock = z
    .object({
    type: z.literal('stacked_bar_chart'),
    title: z.string().optional(),
    unit: z.string().optional(),
    legend: z
        .array(z
        .object({
        label: z.string(),
        state: z.enum(['ok', 'warn', 'alert', 'info', 'neutral']).optional(),
    })
        .strict())
        .optional(),
    bars: z.array(z
        .object({
        label: z.string(),
        value: z.string().optional(),
        segments: z.array(z
            .object({
            label: z.string(),
            value: z.number().min(0),
            state: z.enum(['ok', 'warn', 'alert', 'info', 'neutral']).optional(),
        })
            .strict()),
    })
        .strict()),
})
    .strict();
// Non-escape-hatch blocks. Used directly and as fallbacks (keeps fallbacks
// non-recursive — a fallback can never itself be a diagram/unsupported block).
const simpleBlockMembers = [
    markdownBlock,
    itemListBlock,
    calloutBlock,
    metricGridBlock,
    tableBlock,
    timelineBlock,
    linkListBlock,
    keyValueBlock,
    quoteBlock,
    barChartBlock,
    stackedBarChartBlock,
];
export const SimpleBlockSchema = z.discriminatedUnion('type', simpleBlockMembers);
const diagramBlock = z
    .object({
    type: z.literal('diagram'),
    variant: z.literal('mermaid'),
    title: z.string().optional(),
    code: z.string(),
    fallback_block: SimpleBlockSchema,
})
    .strict();
const unsupportedBlock = z
    .object({
    type: z.literal('unsupported_visualization_request'),
    requested_type: z.string(),
    signature: z.string().optional(),
    reason: z.string(),
    fallback_block: SimpleBlockSchema,
})
    .strict();
export const BlockSchema = z.discriminatedUnion('type', [
    ...simpleBlockMembers,
    diagramBlock,
    unsupportedBlock,
]);
/**
 * Every block type the digest contract accepts, derived from the union so a
 * newly added block is automatically discoverable (e.g. in the save_digest
 * tool description) without a second list to maintain.
 */
export const DIGEST_BLOCK_TYPES = BlockSchema.options.map((option) => option.shape.type.value);
// ---------------------------------------------------------------------------
// Sections + report
// ---------------------------------------------------------------------------
export const SectionSchema = z
    .object({
    title: z.string(),
    order: z.number().optional(),
    blocks: z.array(BlockSchema),
})
    .strict();
const FeedbackEffectSchema = z
    .object({
    item_id: z.string(),
    reason: z.string(),
    until: z.string().optional(),
    title: z.string().optional(),
})
    .strict();
export const DigestReportSchema = z
    .object({
    schema_version: z.literal(1),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
    generated_at: z.string(),
    refreshed_at: z.string().optional(),
    title: z.string().optional(),
    sources: z.array(z.string()),
    // keyed by section id (e.g. "needs_you") so JSON pointers are stable:
    // /sections/needs_you/blocks/0/items/2/item_id
    sections: z.record(z.string().regex(/^[a-z0-9_]+$/u), SectionSchema),
    feedback_effects: z
        .object({
        suppressed: z.array(FeedbackEffectSchema).default([]),
        collapsed: z.array(FeedbackEffectSchema).default([]),
    })
        .strict()
        .optional(),
})
    .strict();
